import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import type { OutreachProfileDto } from '../outreachTypes';
import type { ProfileUpdate } from '../hooks/useProfileActions';
import { ArtistSearch } from './ArtistSearch';
import { profileMessages as messages } from './profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

type SimilarArtist = OutreachProfileDto['similarArtists'][number];

/** The source line of a similar artist (design outreach b1 note): co-occurrence, genre, you, or Plug. */
export function similarSourceText(intl: IntlShape, artist: SimilarArtist): string {
  switch (artist.source) {
    case 'cooccurrence':
      return artist.sharedPlaylists === undefined
        ? intl.formatMessage(messages.sourceCooccurrenceNoCount)
        : intl.formatMessage(messages.sourceCooccurrence, { count: artist.sharedPlaylists });
    case 'genre':
      return intl.formatMessage(messages.sourceGenre);
    case 'user':
      return intl.formatMessage(messages.sourceUser);
    case 'ai':
    default:
      return intl.formatMessage(messages.sourceAi);
  }
}

interface ProfileSimilarCardProps {
  profile: OutreachProfileDto;
  onSave: (patch: ProfileUpdate) => void;
  disabled: boolean;
}

/**
 * "You sound like" (design outreach b1, b3): each artist names its source; a removed artist never
 * comes back from a rebuild. With no artist, the info line asks for 3 names.
 */
const ProfileSimilarCardComponent: React.FC<ProfileSimilarCardProps> = ({ profile, onSave, disabled }) => {
  const intl = useIntl();
  const known = useMemo(
    () => new Set(profile.similarArtists.map((artist) => artist.musicapiArtistId)),
    [profile.similarArtists]
  );
  const rows = useMemo(
    () =>
      profile.similarArtists.map((artist) => (
        <div key={artist.musicapiArtistId} className={styles.miniRow} data-testid="profile-similar-row">
          <div className={styles.grow}>
            <b>{artist.name}</b>
          </div>
          <span className={clsx(styles.srcChip)}>{similarSourceText(intl, artist)}</span>
          {!disabled && (
            <button
              type="button"
              className="btn btn-link p-0 text-body-secondary"
              aria-label={intl.formatMessage(messages.remove, { name: artist.name })}
              onClick={() => onSave({ similarArtists: { remove: [artist.musicapiArtistId] } })}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      )),
    [disabled, intl, onSave, profile.similarArtists]
  );

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.similarTitle)}
        right={
          <span className={styles.small}>
            {intl.formatMessage(messages.similarMeta, { count: profile.similarArtists.length })}
          </span>
        }
      />
      <PanelBody>
        <div className={styles.stack}>
          {profile.similarArtists.length === 0 && (
            <div className={styles.alert} data-testid="profile-no-similar">
              <FontAwesomeIcon icon={faCircleInfo} className={styles.alertIcon} />
              <span>{intl.formatMessage(messages.noSimilar)}</span>
            </div>
          )}
          <div className={styles.miniList}>{rows}</div>
          {!disabled && <ArtistSearch known={known} onAdd={(id) => onSave({ similarArtists: { add: [id] } })} />}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const ProfileSimilarCard = TypedMemo(ProfileSimilarCardComponent);
